import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  X, Hash, Loader, AlertCircle, CheckCircle, Edit2, Save, RefreshCw
} from 'lucide-react';
import {
  GET_RESOLUCIONES,
  CREAR_RESOLUCION,
  ACTUALIZAR_RESOLUCION,
  GET_FACTUS_NUMBERING_RANGES
} from '../../graphql/consecutivos';
import './ModalConfig.css';

function ModalConsecutivos({ onClose }) {
  const [mensaje, setMensaje] = useState(null);
  const [editando, setEditando] = useState(false);
  const [formData, setFormData] = useState({});
  const [rangosFactus, setRangosFactus] = useState([]);
  const [cargandoRangos, setCargandoRangos] = useState(false);
  const autoCreado = useRef(false);

  const { data, loading, error, refetch } = useQuery(GET_RESOLUCIONES);

  // Auto-crear consecutivo POS por defecto si no existe
  const [crearResolucion, { loading: creando }] = useMutation(CREAR_RESOLUCION, {
    onCompleted: () => {
      refetch();
    },
    onError: (err) => {
      setMensaje({ tipo: 'error', texto: `Error al crear consecutivo: ${err.message}` });
    }
  });

  useEffect(() => {
    if (!data || loading || autoCreado.current) return;
    const tieneFactura = (data.resoluciones || []).some(r => r.tipo_documento === 'factura');
    if (!tieneFactura) {
      autoCreado.current = true;
      crearResolucion({
        variables: {
          input: {
            tipo_documento: 'factura',
            nombre: 'Consecutivo POS',
            prefijo: 'POS',
            numero_inicial: '1',
            numero_final: '999999999',
            numero_actual: '1',
            activo: true,
            transmision_automatica: false
          }
        }
      });
    }
  }, [data, loading]);

  const [cargarRangosFactus] = useLazyQuery(GET_FACTUS_NUMBERING_RANGES, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setCargandoRangos(false);
      const rangos = data?.factusNumberingRanges || [];
      setRangosFactus(rangos);
      if (rangos.length > 0) {
        setMensaje({ tipo: 'success', texto: `Se encontraron ${rangos.length} rangos en Factus` });
      } else {
        setMensaje({ tipo: 'error', texto: 'No se encontraron rangos en Factus' });
      }
      setTimeout(() => setMensaje(null), 3000);
    },
    onError: (err) => {
      setCargandoRangos(false);
      setMensaje({ tipo: 'error', texto: `Error al cargar rangos: ${err.message}` });
      setTimeout(() => setMensaje(null), 5000);
    }
  });

  const [actualizarResolucion, { loading: actualizando }] = useMutation(ACTUALIZAR_RESOLUCION, {
    onCompleted: () => {
      setMensaje({ tipo: 'success', texto: 'Consecutivo actualizado exitosamente' });
      refetch();
      setEditando(false);
      setTimeout(() => setMensaje(null), 3000);
    },
    onError: (err) => {
      setMensaje({ tipo: 'error', texto: err.message });
      setTimeout(() => setMensaje(null), 5000);
    },
  });

  // Obtener la resolución POS (factura) activa
  const resoluciones = data?.resoluciones || [];
  const resolucionPOS = resoluciones.find(r => r.tipo_documento === 'factura' && r.activo)
    || resoluciones.find(r => r.tipo_documento === 'factura');

  const handleEditar = () => {
    if (!resolucionPOS) return;
    setFormData({
      nombre: resolucionPOS.nombre,
      resolucion: resolucionPOS.resolucion || '',
      prefijo: resolucionPOS.prefijo,
      numero_inicial: resolucionPOS.numero_inicial,
      numero_final: resolucionPOS.numero_final,
      numero_actual: resolucionPOS.numero_actual,
      fecha_inicio: resolucionPOS.fecha_inicio ? resolucionPOS.fecha_inicio.split('T')[0] : '',
      fecha_fin: resolucionPOS.fecha_fin ? resolucionPOS.fecha_fin.split('T')[0] : '',
      factus_numbering_range_id: resolucionPOS.factus_numbering_range_id?.toString() || ''
    });
    setEditando(true);
    setRangosFactus([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await actualizarResolucion({
      variables: {
        id: resolucionPOS.id,
        input: {
          tipo_documento: 'factura',
          nombre: formData.nombre,
          resolucion: formData.resolucion || null,
          prefijo: formData.prefijo,
          numero_inicial: formData.numero_inicial,
          numero_final: formData.numero_final,
          numero_actual: formData.numero_actual || formData.numero_inicial,
          fecha_inicio: formData.fecha_inicio || null,
          fecha_fin: formData.fecha_fin || null,
          activo: true,
          factus_numbering_range_id: formData.factus_numbering_range_id ? parseInt(formData.factus_numbering_range_id) : null
        }
      }
    });
  };

  const handleCargarRangos = () => {
    setCargandoRangos(true);
    setMensaje(null);
    cargarRangosFactus();
  };

  const handleSeleccionarRango = (rango) => {
    setFormData(prev => ({
      ...prev,
      factus_numbering_range_id: rango.id.toString(),
      prefijo: rango.prefix || prev.prefijo,
      numero_inicial: rango.from?.toString() || prev.numero_inicial,
      numero_final: rango.to?.toString() || prev.numero_final,
      numero_actual: rango.current?.toString() || prev.numero_actual,
      resolucion: rango.resolution_number || prev.resolucion
    }));
  };

  const formatNumero = (num) => parseInt(num).toLocaleString('es-CO');

  const renderProgressBar = (porcentaje) => {
    const color = porcentaje > 90 ? '#ef4444' : porcentaje > 70 ? '#f59e0b' : '#10b981';
    return (
      <div className="progress-bar-container">
        <div
          className="progress-bar-fill"
          style={{ width: `${Math.min(porcentaje, 100)}%`, backgroundColor: color }}
        />
        <span className="progress-bar-text">{porcentaje.toFixed(2)}%</span>
      </div>
    );
  };

  // Loading state
  if (loading || creando) {
    return (
      <div className="modal-overlay">
        <div className="modal-config">
          <div className="modal-config__header">
            <div className="modal-config__header-icon"><Hash size={24} /></div>
            <div>
              <h2 className="modal-config__title">Consecutivo POS</h2>
              <p className="modal-config__subtitle">Cargando...</p>
            </div>
          </div>
          <div className="modal-config__form" style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
            <Loader className="spinner" size={40} />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-config">
          <div className="modal-config__header">
            <div className="modal-config__header-icon"><Hash size={24} /></div>
            <div><h2 className="modal-config__title">Consecutivo POS</h2></div>
            <button className="modal-config__close" onClick={onClose}><X size={20} /></button>
          </div>
          <div className="modal-config__form">
            <div className="alert alert--error">
              <AlertCircle size={20} />
              Error: {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-config modal-config--large">
        <div className="modal-config__header">
          <div className="modal-config__header-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}>
            <Hash size={24} />
          </div>
          <div>
            <h2 className="modal-config__title">Consecutivo POS</h2>
            <p className="modal-config__subtitle">Numeración de facturas electrónicas</p>
          </div>
          <button className="modal-config__close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-config__form">
          {mensaje && (
            <div className={`alert alert--${mensaje.tipo}`}>
              {mensaje.tipo === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
              {mensaje.texto}
            </div>
          )}

          {!resolucionPOS ? (
            <div className="empty-state" style={{ padding: '2rem' }}>
              <Hash size={48} style={{ color: '#9ca3af' }} />
              <h3>No hay consecutivo POS configurado</h3>
              <p>Contacte al administrador del sistema para configurar la numeración.</p>
            </div>
          ) : !editando ? (
            /* Vista de solo lectura */
            <>
              <div className={`consecutivo-card ${resolucionPOS.activo ? 'consecutivo-card--activo' : ''}`}>
                <div className="consecutivo-card__header">
                  <div className="consecutivo-card__info">
                    <span className="consecutivo-card__nombre">{resolucionPOS.nombre}</span>
                    <span className={`badge ${resolucionPOS.activo ? 'badge--success' : 'badge--danger'}`}>
                      {resolucionPOS.activo ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  <button
                    className="config-table__btn config-table__btn--edit"
                    onClick={handleEditar}
                    title="Editar"
                  >
                    <Edit2 size={16} />
                  </button>
                </div>

                <div className="consecutivo-card__details">
                  <div className="consecutivo-card__detail">
                    <span className="detail-label">Resolución DIAN:</span>
                    <span className="detail-value">{resolucionPOS.resolucion || 'N/A'}</span>
                  </div>
                  <div className="consecutivo-card__detail">
                    <span className="detail-label">Prefijo:</span>
                    <span className="detail-value" style={{ fontWeight: 600, color: '#10b981' }}>
                      {resolucionPOS.prefijo}
                    </span>
                  </div>
                  <div className="consecutivo-card__detail">
                    <span className="detail-label">Rango:</span>
                    <span className="detail-value">
                      {formatNumero(resolucionPOS.numero_inicial)} - {formatNumero(resolucionPOS.numero_final)}
                    </span>
                  </div>
                  <div className="consecutivo-card__detail">
                    <span className="detail-label">Número Actual:</span>
                    <span className="detail-value" style={{ fontWeight: 600 }}>
                      {formatNumero(resolucionPOS.numero_actual)}
                    </span>
                  </div>
                  <div className="consecutivo-card__detail">
                    <span className="detail-label">Disponibles:</span>
                    <span className="detail-value">
                      {formatNumero(resolucionPOS.numeros_disponibles)}
                    </span>
                  </div>
                  {resolucionPOS.factus_numbering_range_id && (
                    <div className="consecutivo-card__detail">
                      <span className="detail-label">Rango Factus:</span>
                      <span className="detail-value" style={{ fontWeight: 600, color: '#8b5cf6' }}>
                        ID: {resolucionPOS.factus_numbering_range_id}
                      </span>
                    </div>
                  )}
                </div>

                <div className="consecutivo-card__progress">
                  {renderProgressBar(resolucionPOS.porcentaje_uso)}
                </div>

                {resolucionPOS.fecha_inicio && (
                  <div className="consecutivo-card__vigencia">
                    Vigencia: {new Date(resolucionPOS.fecha_inicio).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' })}
                    {resolucionPOS.fecha_fin && ` - ${new Date(resolucionPOS.fecha_fin).toLocaleDateString('es-CO', { timeZone: 'America/Bogota' })}`}
                  </div>
                )}

                {resolucionPOS.porcentaje_uso > 90 && (
                  <div className="alert alert--warning" style={{ marginTop: '0.5rem', padding: '0.5rem' }}>
                    <AlertCircle size={16} />
                    Rango próximo a agotarse
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Formulario de edición */
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-section">
                  <h4 className="form-section__title">Información Básica</h4>

                  <div className="form-field">
                    <label>Nombre Descriptivo *</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      placeholder="Ej: Facturas POS 2026"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Número de Resolución DIAN</label>
                    <input
                      type="text"
                      value={formData.resolucion}
                      onChange={(e) => setFormData({ ...formData, resolucion: e.target.value })}
                      placeholder="Ej: 18760000001"
                    />
                  </div>

                  <div className="form-field">
                    <label>Prefijo *</label>
                    <input
                      type="text"
                      value={formData.prefijo}
                      onChange={(e) => setFormData({ ...formData, prefijo: e.target.value.toUpperCase() })}
                      placeholder="Ej: SETP"
                      maxLength={10}
                      required
                    />
                    <span className="form-field__help">Aparecerá antes del número (ej: SETP990000001)</span>
                  </div>

                  <div className="form-field">
                    <label>Rango Factus (Numbering Range ID)</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                      <input
                        type="number"
                        value={formData.factus_numbering_range_id}
                        onChange={(e) => setFormData({ ...formData, factus_numbering_range_id: e.target.value })}
                        placeholder="ID del rango en Factus"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn-secondary"
                        onClick={handleCargarRangos}
                        disabled={cargandoRangos}
                        style={{ whiteSpace: 'nowrap', padding: '0.5rem 0.75rem', fontSize: '0.8rem' }}
                      >
                        {cargandoRangos ? <Loader className="spinner" size={14} /> : <RefreshCw size={14} />}
                        {cargandoRangos ? ' Cargando...' : ' Cargar Rangos'}
                      </button>
                    </div>
                    <span className="form-field__help">
                      ID del rango en Factus. Indica qué prefijo y consecutivo usar al transmitir.
                    </span>
                  </div>

                  {rangosFactus.length > 0 && (
                    <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '0.75rem', marginTop: '0.25rem' }}>
                      <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: '#1e40af' }}>
                        Rangos disponibles en Factus:
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        {rangosFactus.map(rango => (
                          <div
                            key={rango.id}
                            onClick={() => handleSeleccionarRango(rango)}
                            style={{
                              background: formData.factus_numbering_range_id === rango.id.toString() ? '#dbeafe' : 'white',
                              border: formData.factus_numbering_range_id === rango.id.toString() ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                              borderRadius: '6px',
                              padding: '0.5rem 0.75rem',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              transition: 'all 0.15s'
                            }}
                          >
                            <div>
                              <strong>ID: {rango.id}</strong> — Prefijo: <strong>{rango.prefix || 'N/A'}</strong>
                              <span style={{ color: '#6b7280', marginLeft: '0.5rem' }}>
                                ({rango.from?.toLocaleString()} - {rango.to?.toLocaleString()})
                              </span>
                            </div>
                            <div style={{ textAlign: 'right', fontSize: '0.75rem' }}>
                              <span style={{ color: '#6b7280' }}>Actual: {rango.current?.toLocaleString()}</span>
                              {rango.is_expired && (
                                <span style={{ color: '#ef4444', marginLeft: '0.5rem', fontWeight: 600 }}>EXPIRADO</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: '#6b7280' }}>
                        Haz clic en un rango para auto-llenar los campos.
                      </p>
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <h4 className="form-section__title">Rango de Numeración</h4>

                  <div className="form-field">
                    <label>Número Inicial *</label>
                    <input
                      type="number"
                      value={formData.numero_inicial}
                      onChange={(e) => setFormData({ ...formData, numero_inicial: e.target.value })}
                      placeholder="Ej: 990000001"
                      min="1"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Número Final *</label>
                    <input
                      type="number"
                      value={formData.numero_final}
                      onChange={(e) => setFormData({ ...formData, numero_final: e.target.value })}
                      placeholder="Ej: 999999999"
                      min="1"
                      required
                    />
                  </div>

                  <div className="form-field">
                    <label>Número Actual</label>
                    <input
                      type="number"
                      value={formData.numero_actual}
                      onChange={(e) => setFormData({ ...formData, numero_actual: e.target.value })}
                      placeholder="Siguiente número a usar"
                      min="1"
                    />
                    <span className="form-field__help">Siguiente número que se asignará a una factura</span>
                  </div>

                  <div className="form-row">
                    <div className="form-field">
                      <label>Fecha Inicio Vigencia</label>
                      <input
                        type="date"
                        value={formData.fecha_inicio}
                        onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                      />
                    </div>
                    <div className="form-field">
                      <label>Fecha Fin Vigencia</label>
                      <input
                        type="date"
                        value={formData.fecha_fin}
                        onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                      />
                    </div>
                  </div>

                </div>
              </div>

              <div className="modal-config__footer" style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
                <button type="button" className="btn-secondary" onClick={() => setEditando(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={actualizando}>
                  {actualizando ? (
                    <><Loader className="spinner" size={18} /> Guardando...</>
                  ) : (
                    <><Save size={18} /> Guardar Cambios</>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {!editando && (
          <div className="modal-config__footer">
            <button className="btn-secondary" onClick={onClose}>Cerrar</button>
          </div>
        )}
      </div>

      <style>{`
        .consecutivo-card {
          background: var(--hotel-card-background, white);
          border-radius: 8px;
          padding: 1rem;
          border: 1px solid var(--hotel-border, #e5e7eb);
          transition: all 0.2s;
        }
        .consecutivo-card--activo {
          border-color: #10b981;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.1);
        }
        .consecutivo-card__header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.75rem;
        }
        .consecutivo-card__info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .consecutivo-card__nombre {
          font-weight: 600;
          color: var(--hotel-text, #1f2937);
        }
        .consecutivo-card__details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 0.5rem;
          margin-bottom: 0.75rem;
        }
        .consecutivo-card__detail {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
        }
        .detail-label {
          font-size: 0.75rem;
          color: var(--hotel-text-secondary, #6b7280);
        }
        .detail-value {
          font-size: 0.875rem;
          color: var(--hotel-text, #1f2937);
        }
        .consecutivo-card__progress {
          margin-bottom: 0.5rem;
        }
        .progress-bar-container {
          position: relative;
          height: 20px;
          background: var(--hotel-border, #e5e7eb);
          border-radius: 10px;
          overflow: hidden;
        }
        .progress-bar-fill {
          height: 100%;
          border-radius: 10px;
          transition: width 0.3s ease;
        }
        .progress-bar-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--hotel-text, #374151);
        }
        .consecutivo-card__vigencia {
          font-size: 0.75rem;
          color: var(--hotel-text-secondary, #6b7280);
          margin-top: 0.25rem;
        }
      `}</style>
    </div>
  );
}

export default ModalConsecutivos;
